import { useCrosswordSettings } from '../contexts/CrosswordSettingsContext'
import type { Size, CrosswordColors } from '../contexts/CrosswordSettingsContext'
import { CrosswordWord, CrosswordPair } from './CrosswordText'
import { debugPanel } from '../lib/Z'

const SIZES: Size[] = ['2xs', 'xs', 'sm', 'base', 'lg', 'xl', '2xl', '3xl']

const COLOR_LABELS: Record<keyof CrosswordColors, string> = {
  normalBg: 'Normal Background',
  normalBorder: 'Normal Border',
  normalText: 'Normal Text',
  invertedBg: 'Inverted Background',
  invertedBorder: 'Inverted Border',
  invertedText: 'Inverted Text',
  horizontalHighlightBg: 'Horizontal Highlight Bg',
  horizontalHighlightBorder: 'Horizontal Highlight Border',
  verticalHighlightBg: 'Vertical Highlight Bg',
  verticalHighlightBorder: 'Vertical Highlight Border',
  conflictBg: 'Conflict Background',
}

export function CrosswordDebugPanel() {
  const { size, setSize, colors, updateColor, resetColors, showDebug, setShowDebug } = useCrosswordSettings()

  if (!showDebug) {
    return (
      <button
        onClick={() => setShowDebug(true)}
        className="fixed bottom-4 right-4 w-10 h-10 bg-gray-900 text-white rounded-full shadow-lg hover:bg-gray-700 transition-colors text-xs font-mono"
        style={{ zIndex: debugPanel }}
        title="Open debug panel"
      >
        ⚙
      </button>
    )
  }

  return (
    <div className="fixed bottom-4 right-4 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 max-h-[80vh] overflow-hidden flex flex-col" style={{ zIndex: debugPanel }}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between shrink-0">
        <h3 className="font-semibold text-gray-900">Crossword Settings</h3>
        <button
          onClick={() => setShowDebug(false)}
          className="text-gray-400 hover:text-gray-600"
        >
          ✕
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Size selector */}
        <div>
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-2">
            Size
          </label>
          <div className="flex flex-wrap gap-1">
            {SIZES.map(s => (
              <button
                key={s}
                onClick={() => setSize(s)}
                className={`px-3 py-1.5 text-xs rounded-md transition-colors ${size === s
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Preview */}
        <div>
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-2">
            Preview
          </label>
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 w-16">Word:</span>
              <CrosswordWord text="EXAMPLE" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 w-16">Inverted:</span>
              <CrosswordWord text="EXAMPLE" inverted />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 w-16">Pair:</span>
              <CrosswordPair textA="CAT" textB="DOG" />
            </div>
          </div>
        </div>

        {/* Colors */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Colors
            </label>
            <button
              onClick={resetColors}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              Reset
            </button>
          </div>
          <div className="space-y-2">
            {(Object.keys(COLOR_LABELS) as (keyof CrosswordColors)[]).map(key => (
              <div key={key} className="flex items-center gap-2">
                <input
                  type="color"
                  value={colors[key]}
                  onChange={e => updateColor(key, e.target.value)}
                  className="w-8 h-8 rounded cursor-pointer border border-gray-200"
                />
                <span className="text-xs text-gray-600 flex-1">{COLOR_LABELS[key]}</span>
                <input
                  type="text"
                  value={colors[key]}
                  onChange={e => updateColor(key, e.target.value)}
                  className="w-20 px-2 py-1 text-xs font-mono border border-gray-200 rounded"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
